import { createAsyncThunk } from "@reduxjs/toolkit";
import { ApiErrorResponse } from "../../../api/types/ApiErrorResponse";
import { toApiError } from "../../../api/utils/errorHandling";
import axiosInstance from "../../axiosInstance/axiosInstance";
import { MemberSeatResponse } from "../contracts/MemberSeatResponse";
import { OccurrenceOccupancyResponse } from "../contracts/OccurrenceOccupancyResponse";
import { OccupancyResponseMapper } from "../mappers/OccupancyResponseMapper";
import { MemberSeat } from "../models/MemberSeat";
import { OccurrenceOccupancy } from "../models/OccurrenceOccupancy";
import { SessionResponse } from "../contracts/SessionResponse";
import { SessionResponseMapper } from "../mappers/SessionResponseMapper";
import { Session } from "../models/Session";

const mapper = new OccupancyResponseMapper();
const sessionMapper = new SessionResponseMapper();

/** How full each upcoming date of a Session is in [from, to] (server default: the next eight weeks). */
export const fetchSessionOccupancy = createAsyncThunk<
  OccurrenceOccupancy[],
  { bookingId: string; from?: string; to?: string },
  { rejectValue: ApiErrorResponse }
>("seats/fetchOccupancy", async ({ bookingId, from, to }, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.get<OccurrenceOccupancyResponse[]>(`/bookings/${bookingId}/occupancy`, {
      params: { from, to },
    });
    return mapper.toOccupancies(response.data);
  } catch (error) {
    return rejectWithValue(toApiError(error));
  }
});

/**
 * One Session for the "Book a date" picker's roster check. Unlike fetchSessionById it
 * leaves the sessions slice's current Session alone (the member page is not a Session page).
 */
export const fetchSessionForBooking = createAsyncThunk<Session, string, { rejectValue: ApiErrorResponse }>(
  "seats/fetchSessionForBooking",
  async (bookingId, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get<SessionResponse>(`/bookings/${bookingId}`);
      return sessionMapper.toSession(response.data);
    } catch (error) {
      return rejectWithValue(toApiError(error));
    }
  },
);

/** The member's booked single dates from today on. */
export const fetchMemberUpcomingSeats = createAsyncThunk<
  MemberSeat[],
  string,
  { rejectValue: ApiErrorResponse }
>("seats/fetchMemberUpcoming", async (memberId, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.get<MemberSeatResponse[]>(`/members/${memberId}/upcoming-seats`);
    return mapper.toMemberSeats(response.data);
  } catch (error) {
    return rejectWithValue(toApiError(error));
  }
});
